import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formasPagoAPI, cajasAPI, cuentasBancariasAPI, usuariosAPI } from '../services/api';
import { toast } from 'react-toastify';
import '../Clientes.css';

const MODO_SIN_PREDEFINIDO = '__sin_predefinido__';
const MODO_USAR_DEFAULT = '__usar_default__';

const buildFondoValue = (cajaId, cuentaId) => {
  if (cajaId) return `caja_${cajaId}`;
  if (cuentaId) return `cuenta_${cuentaId}`;
  return '';
};

const parseFondoValue = (val) => {
  if (!val) return { caja_id: null, cuenta_bancaria_id: null };
  if (val.startsWith('caja_')) {
    const id = parseInt(val.replace('caja_', ''), 10);
    return { caja_id: isNaN(id) ? null : id, cuenta_bancaria_id: null };
  }
  if (val.startsWith('cuenta_')) {
    const id = parseInt(val.replace('cuenta_', ''), 10);
    return { caja_id: null, cuenta_bancaria_id: isNaN(id) ? null : id };
  }
  return { caja_id: null, cuenta_bancaria_id: null };
};

const buildSelectValueFromRow = (row) => {
  if (!row) return MODO_USAR_DEFAULT;
  const modo = (row.modo_asignacion || '').toLowerCase();
  if (modo === 'sin_predefinido') return MODO_SIN_PREDEFINIDO;
  if (modo === 'usar_default') return MODO_USAR_DEFAULT;
  if (modo === 'explicito' || row.caja_id || row.cuenta_bancaria_id) {
    return buildFondoValue(row.caja_id, row.cuenta_bancaria_id) || MODO_USAR_DEFAULT;
  }
  return MODO_USAR_DEFAULT;
};

const parseSelectValueToPayload = (val) => {
  if (val === MODO_SIN_PREDEFINIDO) {
    return { modo_asignacion: 'sin_predefinido', caja_id: null, cuenta_bancaria_id: null };
  }
  if (val === MODO_USAR_DEFAULT) {
    return { modo_asignacion: 'usar_default', caja_id: null, cuenta_bancaria_id: null };
  }
  const { caja_id, cuenta_bancaria_id } = parseFondoValue(val);
  return { modo_asignacion: 'explicito', caja_id, cuenta_bancaria_id };
};

const FormaPagoFondosUsuario = ({ adminMode = false }) => {
  const [formasPago, setFormasPago] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [asignaciones, setAsignaciones] = useState({});
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioId, setUsuarioId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadUsuarios = useCallback(async () => {
    if (!adminMode) return;
    try {
      const res = await usuariosAPI.getAll();
      if (res.success && Array.isArray(res.data)) {
        const activos = res.data.filter(u => u.activo && u.username !== 'admin');
        setUsuarios(activos);
        if (activos.length && !usuarioId) {
          setUsuarioId(String(activos[0].id));
        }
      }
    } catch {
      setUsuarios([]);
    }
  }, [adminMode, usuarioId]);

  const loadData = useCallback(async () => {
    if (adminMode && !usuarioId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const asignPromise = adminMode
        ? usuariosAPI.getFormaPagoFondosForUser(usuarioId).catch(() => ({ success: true, data: [] }))
        : usuariosAPI.getFormaPagoFondos().catch(() => ({ success: true, data: [] }));

      const fondoParams = { activo: true };
      if (adminMode && usuarioId) fondoParams.usuario_id = usuarioId;

      const [fpRes, cajasRes, cuentasRes, asignRes] = await Promise.all([
        formasPagoAPI.getAll({ activo: true }),
        cajasAPI.getAll(fondoParams),
        cuentasBancariasAPI.getAll(fondoParams),
        asignPromise,
      ]);

      const fps = fpRes.success && Array.isArray(fpRes.data) ? fpRes.data : [];
      setFormasPago(fps);

      if (cajasRes.success && Array.isArray(cajasRes.data)) {
        setCajas(cajasRes.data);
      } else {
        setCajas([]);
      }
      if (cuentasRes.success && Array.isArray(cuentasRes.data)) {
        setCuentas(cuentasRes.data);
      } else {
        setCuentas([]);
      }

      const asignRows = asignRes && asignRes.success && Array.isArray(asignRes.data) ? asignRes.data : [];
      const map = {};
      for (const fp of fps) {
        const row = asignRows.find((r) => r.forma_pago_id === fp.id);
        map[fp.id] = buildSelectValueFromRow(row);
      }
      setAsignaciones(map);
    } catch (e) {
      console.error('Error al cargar configuración de fondos por forma de pago:', e);
      toast.error('Error al cargar configuración de fondos');
      setFormasPago([]);
      setCajas([]);
      setCuentas([]);
      setAsignaciones({});
    } finally {
      setLoading(false);
    }
  }, [adminMode, usuarioId]);

  useEffect(() => {
    if (adminMode) loadUsuarios();
  }, [adminMode, loadUsuarios]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleChangeFondo = (formaId, value) => {
    setAsignaciones((prev) => ({ ...prev, [formaId]: value }));
  };

  const handleGuardar = async () => {
    try {
      setSaving(true);
      const items = formasPago.map((fp) => ({
        forma_pago_id: fp.id,
        ...parseSelectValueToPayload(asignaciones[fp.id] ?? MODO_USAR_DEFAULT),
      }));
      if (adminMode) {
        if (!usuarioId) {
          toast.error('Seleccione un usuario');
          return;
        }
        await usuariosAPI.saveFormaPagoFondosForUser(usuarioId, items);
      } else {
        await usuariosAPI.saveFormaPagoFondos(items);
      }
      toast.success('Asignación de fondos guardada');
    } catch (e) {
      console.error('Error al guardar asignación fondos por forma de pago:', e);
      toast.error(e.message || 'Error al guardar asignación');
    } finally {
      setSaving(false);
    }
  };

  const opcionesFondo = useMemo(() => {
    const opts = [
      { value: MODO_SIN_PREDEFINIDO, label: '— Sin caja predefinida (elegir al operar) —' },
      { value: MODO_USAR_DEFAULT, label: '— Usar destino por defecto de la forma de pago —' },
    ];
    if (cajas.length) {
      opts.push({ value: '__group_cajas__', label: '--- Cajas (acceso del usuario) ---', disabled: true });
      cajas.forEach((c) => {
        opts.push({
          value: `caja_${c.id}`,
          label: `Caja: ${c.nombre}`,
        });
      });
    }
    if (cuentas.length) {
      opts.push({ value: '__group_cuentas__', label: '--- Cuentas bancarias (acceso del usuario) ---', disabled: true });
      cuentas.forEach((cb) => {
        opts.push({
          value: `cuenta_${cb.id}`,
          label: `Cuenta: ${cb.banco_nombre || 'Banco'} - ${cb.numero_cuenta}`,
        });
      });
    }
    return opts;
  }, [cajas, cuentas]);

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="clientes pagina-listado pagina-listado--scroll">
      <div className="page-header">
        <h1>{adminMode ? 'Matriz cajas por usuario y forma de pago' : 'Fondos por Forma de Pago (usuario actual)'}</h1>
        <button
          type="button"
          className="btn-primary"
          onClick={handleGuardar}
          disabled={saving || (adminMode && !usuarioId)}
        >
          {saving ? 'Guardando...' : 'Guardar asignación'}
        </button>
      </div>

      <div className="pagina-listado-fijo">
      {adminMode && (
        <div style={{ marginBottom: 16 }}>
          <label style={{ marginRight: 8 }}>Usuario:</label>
          <select
            value={usuarioId}
            onChange={e => setUsuarioId(e.target.value)}
            className="search-input"
            style={{ minWidth: 220 }}
          >
            <option value="">Seleccione usuario</option>
            {usuarios.map(u => (
              <option key={u.id} value={u.id}>{u.nombre || u.username}</option>
            ))}
          </select>
        </div>
      )}

      <p style={{ marginBottom: 16 }}>
        {adminMode
          ? 'Para cada forma de pago puede: no predefinir caja (el usuario elige al operar), usar el destino por defecto de la forma de pago, o asignar una caja/cuenta a la que el usuario tenga acceso.'
          : 'Sin caja predefinida: al registrar un gasto o cobro deberá elegir el fondo. Usar destino por defecto: se usa la caja/cuenta configurada en la forma de pago. También puede asignar una caja propia a la que tenga acceso.'}
      </p>
      </div>

      <div className="listado-panel clientes-listado listado-controles">
        <div className="listado-panel-cuerpo clientes-listado-cuerpo">
          <div className="listado-tabla-area clientes-listado-tabla-area">
            <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Forma de pago</th>
              <th>Uso</th>
              <th>Destino por defecto (forma de pago)</th>
              <th>Fondo para {adminMode ? 'el usuario' : 'este usuario'}</th>
            </tr>
          </thead>
          <tbody>
            {formasPago.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-data">No hay formas de pago registradas</td>
              </tr>
            ) : (
              formasPago.map((fp) => {
                const uso =
                  fp.tipo_movimiento === 'ingreso'
                    ? 'Ingreso'
                    : fp.tipo_movimiento === 'egreso'
                    ? 'Egreso'
                    : 'Ambos';
                const destinoPorDefecto = fp.caja_nombre
                  ? `Caja: ${fp.caja_nombre}`
                  : fp.cuenta_banco_nombre && fp.cuenta_numero
                  ? `Cuenta: ${fp.cuenta_banco_nombre} - ${fp.cuenta_numero}`
                  : 'Sin destino específico';
                const value = asignaciones[fp.id] ?? MODO_USAR_DEFAULT;
                return (
                  <tr key={fp.id}>
                    <td><strong>{fp.nombre}</strong></td>
                    <td>{uso}</td>
                    <td>{destinoPorDefecto}</td>
                    <td>
                      <select
                        value={value}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '__group_cajas__' || v === '__group_cuentas__') return;
                          handleChangeFondo(fp.id, v);
                        }}
                      >
                        {opcionesFondo.map((opt) => (
                          <option
                            key={opt.value || opt.label}
                            value={opt.value}
                            disabled={opt.disabled}
                          >
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormaPagoFondosUsuario;
